import { useState } from 'react';
import { Alert, App, Button, Card, Form, Modal, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { CreateAllyBody } from '@goelectric/shared-types';
import { allyApi } from '../api/allyApi';
import { AllyForm } from '../components/AllyForm';
import { InviteLinksModal, type InvitedAdmin } from '../components/InviteLinksModal';
import { describeAllyApiError } from '../utils/allyErrors';
import type { CreateAllyFormState } from '../utils/fiscal';

/**
 * Página de alta de aliado (`/aliados/nuevo`, solo `SUPER_ADMIN` — el guard de la ruta
 * lo garantiza). Compone `AllyForm` con el POST y el resultado: en éxito muestra SIEMPRE
 * el modal de confirmación; recién al cerrarlo navega al detalle del aliado recién creado.
 * En error muestra el `message` del backend y marca el campo si el error lo identifica
 * (ADR 0021).
 *
 * No hay proveedor de correo: el link de enrolamiento de cada administrador nace en la
 * respuesta del POST (el token no se persiste en claro). Si el aliado se creó **sin**
 * admins, el modal igual aparece con el aviso de que nadie puede ingresar a su panel y que
 * los admins se agregan desde el detalle (F11).
 */
export function CreateAllyPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<CreateAllyFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Campo que marcó el backend en el último error; el wizard salta a su paso si no es el actual.
  const [errorField, setErrorField] = useState<string | null>(null);
  // Confirmación del alta: siempre presente tras crear; trae el id y los admins con link.
  const [confirmation, setConfirmation] = useState<{ allyId: string; admins: InvitedAdmin[] } | null>(
    null,
  );

  const goToList = () => navigate('/aliados');
  const goToDetail = (allyId: string) => navigate(`/aliados/${encodeURIComponent(allyId)}`);

  /** Cierra el modal (o el X/ESC) y recién ahí navega al detalle del aliado creado. */
  const closeConfirmation = () => {
    const allyId = confirmation?.allyId;
    setConfirmation(null);
    if (allyId) {
      goToDetail(allyId);
    }
  };

  const handleSubmit = async (body: CreateAllyBody) => {
    setSubmitting(true);
    setError(null);
    setErrorField(null);
    try {
      const created = await allyApi.create(body);
      message.success(`Aliado ${created.id} creado.`);
      const invitedAdmins = (created.admins ?? []).filter(
        (admin): admin is InvitedAdmin => Boolean(admin.inviteUrl),
      );
      setConfirmation({ allyId: created.id, admins: invitedAdmins });
    } catch (caught) {
      const { message: detail, field } = describeAllyApiError(caught);
      setError(detail);
      setErrorField(field ?? null);
      if (field) {
        // El backend nombra el campo con la convención del formulario; se marca tal cual.
        form.setFields([{ name: field as keyof CreateAllyFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const invitedAdmins = confirmation?.admins ?? [];
  const hasInvites = invitedAdmins.length > 0;

  return (
    <div className="aliados-page">
      <div className="aliados-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de aliados"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Nuevo aliado
          </Typography.Title>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear el aliado"
          description={error}
        />
      )}

      <Card>
        <AllyForm
          form={form}
          onSubmit={handleSubmit}
          submitting={submitting}
          errorField={errorField}
        />
      </Card>

      {/* Alta sin admins (F11): el modal igual aparece; el detalle es donde se agregan. */}
      <Modal
        open={confirmation !== null && !hasInvites}
        rootClassName="aliados-invite-modal"
        title={
          <Typography.Title level={3} className="aliados-invite-modal__headline" style={{ margin: 0 }}>
            Tenemos un nuevo aliado
          </Typography.Title>
        }
        onCancel={closeConfirmation}
        footer={
          <Button type="primary" onClick={closeConfirmation}>
            Ver detalles del aliado
          </Button>
        }
        width={640}
      >
        <Typography.Paragraph className="aliados-invite-modal__lead">
          El aliado se creó correctamente, pero{' '}
          <Typography.Text strong>todavía no tiene administradores registrados</Typography.Text>. Sin
          ellos, nadie puede ingresar a su panel.
        </Typography.Paragraph>
        <Typography.Paragraph className="aliados-invite-modal__lead" style={{ marginBottom: 0 }}>
          Los administradores se agregan desde el detalle del aliado.
        </Typography.Paragraph>
      </Modal>

      <InviteLinksModal
        open={confirmation !== null && hasInvites}
        admins={invitedAdmins}
        onClose={closeConfirmation}
        footerLabel="Ver detalles del aliado"
      />
    </div>
  );
}
